import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import api, { setTokenGetter } from '../api/client'
import { loginRequest } from '../auth/msalConfig'

const AuthContext = createContext(null)

// Guards against an auto-recovery redirect loop: we only kick off interactive
// re-auth once per browser tab session. Cleared whenever a silent token
// acquisition succeeds, so a normally-expired session can recover again later.
const RECOVERY_FLAG = 'msalRecoveryAttempt'

function normalizeUser(data) {
  if (!data) return null
  const roles = Array.isArray(data.roles) && data.roles.length
    ? data.roles
    : (data.role ? [data.role] : [])
  return { ...data, roles }
}

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal()
  const isSignedIn = useIsAuthenticated()
  const isLoaded = inProgress === InteractionStatus.None

  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // Recover from a silent-acquisition failure by forcing interactive re-auth.
  // A poisoned/incompatible MSAL cache (left over from the Clerk→Entra migration,
  // or a stuck interaction state from an interrupted redirect) makes
  // acquireTokenSilent throw while an account still appears present — so the app
  // looks "signed in" but can never get a usable token. We must drive the user
  // back through login instead of silently giving up.
  const recoverInteractively = useCallback(async () => {
    if (sessionStorage.getItem(RECOVERY_FLAG)) return  // already tried this tab session
    sessionStorage.setItem(RECOVERY_FLAG, '1')
    try {
      await instance.acquireTokenRedirect(loginRequest)
    } catch {
      // Even starting interaction failed (e.g. the interaction-in-progress flag
      // is stuck in storage). Wipe the MSAL cache — this is exactly what users
      // were doing by hand via "clear site data" — then restart a clean login.
      try { await instance.clearCache() } catch {}
      try { await instance.loginRedirect(loginRequest) } catch {}
    }
  }, [instance])

  // Acquire an access token for our API. forceRefresh bypasses the cached access
  // token (used to recover from a stale token the backend rejects) — MSAL mints
  // a new one via the refresh token. Any silent failure falls back to
  // interactive recovery so a user is never stranded on the error banner.
  const getToken = useCallback(async ({ forceRefresh = false } = {}) => {
    const account = instance.getActiveAccount() || accounts[0]
    if (!account) return null
    try {
      const res = await instance.acquireTokenSilent({ ...loginRequest, account, forceRefresh })
      sessionStorage.removeItem(RECOVERY_FLAG)  // healthy session — allow future recovery
      return res.accessToken
    } catch {
      await recoverInteractively()
      return null
    }
  }, [instance, accounts, recoverInteractively])

  useEffect(() => { setTokenGetter(getToken) }, [getToken])

  const refreshUser = useCallback(async () => {
    if (!isSignedIn) { setUser(null); return }
    try {
      const { data } = await api.get('/api/auth/me')
      setUser(normalizeUser(data))
    } catch {
      setUser(null)
    }
  }, [isSignedIn])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!isLoaded) return
      if (!isSignedIn) { setUser(null); setLoading(false); return }
      try {
        const { data } = await api.get('/api/auth/me')
        if (!cancelled) setUser(normalizeUser(data))
      } catch {
        if (!cancelled) setUser(null)
      }
      if (!cancelled) setLoading(false)
    }
    run()
    return () => { cancelled = true }
  }, [isLoaded, isSignedIn])

  const login = () => instance.loginRedirect(loginRequest)
  const logout = () => instance.logoutRedirect()

  const hasRole = useCallback(
    (...wanted) => {
      if (!user?.roles?.length) return false
      return wanted.some(r => user.roles.includes(r))
    },
    [user],
  )

  return (
    <AuthContext.Provider value={{ user, loading, isSignedIn, isLoaded, login, logout, refreshUser, hasRole }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
